/**
 * Purpose: Compose controls + map and bind selected domain/time/layer state to overlay components.
 * Assumptions: Domain metadata is loaded from Open-Meteo latest.json and layer components derive domain data internally.
 * External refs: react-map-gl/maplibre Map API and domain definitions from ./domainConfig.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { ViewState } from "react-map-gl/maplibre";
import { Map, NavigationControl, type MapRef } from "react-map-gl/maplibre";
import "./App.css";
import { DomainBoundOverlay } from "./components/DomainBoundOverlay";
import { DomainGridOverlay } from "./components/DomainGridOverlay";
import { GMapsBaseLayer } from "./components/GMapsBaseLayer";
import { OpenMeteoRasterLayer } from "./components/OpenMeteoRasterLayer";
import { ZLayer } from "./components/ZLayer";
import { FORECAST_DOMAINS } from "./domainConfig";

type ForecastMetadata = {
  reference_time: string;
  valid_times: string[];
  variables: string[];
};

type DebugMapHooks = {
  setView: (viewState: Partial<ViewState>) => void;
  getView: () => { longitude: number; latitude: number; zoom: number } | null;
};

declare global {
  interface Window {
    __VOL_LIBRE_MAP_DEBUG__?: DebugMapHooks;
  }
}

function formatTimestamp(value?: string) {
  if (!value) {
    return "—";
  }

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(value));
}

function App() {
  const [domainId, setDomainId] = useState(FORECAST_DOMAINS[0].id);
  const [selectedHourIndex, setSelectedHourIndex] = useState(0);
  const [metadata, setMetadata] = useState<ForecastMetadata | null>(null);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(true);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [googleBaseLayer, setGoogleBaseLayer] = useState<
    "none" | "p" | "y" | "s"
  >("none");
  const [showDomainOverlay, setShowDomainOverlay] = useState(false);
  const [showDomainGrid, setShowDomainGrid] = useState(false);
  const [selectedWeatherVariable, setSelectedWeatherVariable] = useState<
    "none" | "temperature_2m" | "wind_gusts_10m" | "precipitation"
  >("none");
  const mapRef = useRef<MapRef | null>(null);

  const selectedDomain = useMemo(
    () =>
      FORECAST_DOMAINS.find((domain) => domain.id === domainId) ??
      FORECAST_DOMAINS[0],
    [domainId],
  );

  useEffect(() => {
    const controller = new AbortController();

    fetch(
      `https://map-tiles.open-meteo.com/data_spatial/${selectedDomain.path}/latest.json`,
      {
        signal: controller.signal,
      },
    )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(
            `Open-Meteo metadata request failed (${response.status})`,
          );
        }

        return (await response.json()) as ForecastMetadata;
      })
      .then((nextMetadata) => {
        setMetadata(nextMetadata);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }

        setMetadata(null);
        setMetadataError(
          error instanceof Error
            ? error.message
            : "Unable to load forecast metadata",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoadingMetadata(false);
        }
      });

    return () => controller.abort();
  }, [selectedDomain.path]);

  useEffect(() => {
    mapRef.current?.easeTo({
      center: selectedDomain.center,
      zoom: selectedDomain.zoom,
      duration: 800,
    });
  }, [selectedDomain]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.__VOL_LIBRE_MAP_DEBUG__ = {
      setView: (nextViewState) => {
        const map = mapRef.current;
        if (!map) {
          return;
        }

        const easeOptions: {
          duration: number;
          center?: [number, number];
          zoom?: number;
          bearing?: number;
          pitch?: number;
        } = { duration: 0 };

        if (
          nextViewState.longitude !== undefined &&
          nextViewState.latitude !== undefined
        ) {
          easeOptions.center = [
            nextViewState.longitude,
            nextViewState.latitude,
          ];
        }
        if (nextViewState.zoom !== undefined) {
          easeOptions.zoom = nextViewState.zoom;
        }
        if (nextViewState.bearing !== undefined) {
          easeOptions.bearing = nextViewState.bearing;
        }
        if (nextViewState.pitch !== undefined) {
          easeOptions.pitch = nextViewState.pitch;
        }

        map.easeTo(easeOptions);
      },
      getView: () => {
        const map = mapRef.current;
        if (!map) {
          return null;
        }

        const center = map.getCenter();
        return {
          longitude: center.lng,
          latitude: center.lat,
          zoom: map.getZoom(),
        };
      },
    };

    return () => {
      delete window.__VOL_LIBRE_MAP_DEBUG__;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) {
      return;
    }

    const logLayerOrder = () => {
      if (!map.isStyleLoaded()) {
        return;
      }

      const styleLayers = map.getStyle().layers ?? [];
      console.info(
        "[VolLibreMap] map.getStyle().layers rendering order",
        styleLayers.map((layer, index) => ({
          order: index,
          id: layer.id,
          type: layer.type,
          visibility: layer.layout?.visibility ?? "visible",
        })),
      );
    };

    logLayerOrder();
    map.on("styledata", logLayerOrder);

    return () => {
      map.off("styledata", logLayerOrder);
    };
  }, []);

  const selectedValidTime = metadata?.valid_times[selectedHourIndex];
  const hourCount = metadata?.valid_times.length ?? 0;
  const sliderMax = Math.max(hourCount - 1, 0);
  return (
    <div className="app-shell">
      <aside className="control-panel">
        <div>
          <p className="eyebrow">Vol Libre Map</p>
          <h1>Open-Meteo forecast layers</h1>
          <p className="panel-copy">
            Latest-run weather overlays rendered in MapLibre with React map
            components.
          </p>
        </div>

        <label className="field">
          <span>Forecast domain</span>
          <select
            value={domainId}
            onChange={(event) => {
              setDomainId(event.target.value);
              setMetadata(null);
              setMetadataError(null);
              setIsLoadingMetadata(true);
              setSelectedHourIndex(0);
            }}
          >
            {FORECAST_DOMAINS.map((domain) => (
              <option key={domain.id} value={domain.id}>
                {domain.label}
              </option>
            ))}
          </select>
        </label>

        <fieldset className="field layer-fieldset">
          <legend>Data overlays</legend>
          <label className="field">
            <span>Base layer</span>
            <select
              value={googleBaseLayer}
              onChange={(event) => {
                setGoogleBaseLayer(
                  event.target.value as "none" | "p" | "y" | "s",
                );
              }}
            >
              <option value="none">None</option>
              <option value="p">Terrain</option>
              <option value="y">Hybrid Satellite</option>
              <option value="s">Satellite Only</option>
            </select>
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={showDomainOverlay}
              onChange={(event) => setShowDomainOverlay(event.target.checked)}
            />
            <span>Domain</span>
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={showDomainGrid}
              onChange={(event) => setShowDomainGrid(event.target.checked)}
            />
            <span>Domain Grid</span>
          </label>
          <label className="field">
            <span>Weather variable</span>
            <select
              value={selectedWeatherVariable}
              onChange={(event) => {
                setSelectedWeatherVariable(
                  event.target.value as
                    | "none"
                    | "temperature_2m"
                    | "wind_gusts_10m"
                    | "precipitation",
                );
              }}
            >
              <option value="none">None</option>
              <option value="temperature_2m">Temperature 2m</option>
              <option value="wind_gusts_10m">Wind Gusts 10m</option>
              <option value="precipitation">Precipitation</option>
            </select>
          </label>
        </fieldset>

        <label className="field">
          <span>Display hour</span>
          <input
            type="range"
            min="0"
            max={sliderMax}
            step="1"
            value={Math.min(selectedHourIndex, sliderMax)}
            disabled={hourCount === 0}
            onChange={(event) =>
              setSelectedHourIndex(Number(event.target.value))
            }
          />
          <div className="slider-meta">
            <strong>T+{selectedHourIndex}h</strong>
            <span>{formatTimestamp(selectedValidTime)}</span>
          </div>
        </label>

        <dl className="metadata-list">
          <div>
            <dt>Run time</dt>
            <dd>{formatTimestamp(metadata?.reference_time)}</dd>
          </div>
          <div>
            <dt>Steps</dt>
            <dd>{hourCount || "—"}</dd>
          </div>
        </dl>

        {isLoadingMetadata ? (
          <p className="status">Loading latest run metadata…</p>
        ) : null}
        {metadataError ? <p className="status error">{metadataError}</p> : null}
      </aside>

      <main className="map-panel">
        <Map
          ref={mapRef}
          initialViewState={{
            longitude: selectedDomain.center[0],
            latitude: selectedDomain.center[1],
            zoom: selectedDomain.zoom,
          }}
          mapStyle="https://demotiles.maplibre.org/style.json"
          style={{ width: "100%", height: "100%" }}
        >
          <NavigationControl position="top-right" />

          <ZLayer id="dynamic-domain">
            <DomainBoundOverlay
              visible={showDomainOverlay}
              domainId={selectedDomain.id}
            />
            <DomainGridOverlay
              visible={showDomainGrid}
              domainId={selectedDomain.id}
            />
          </ZLayer>

          <ZLayer id="dynamic-conditions" beforeId="dynamic-domain">
            {selectedWeatherVariable !== "none" && (
              <OpenMeteoRasterLayer
                id="weather-layer"
                url={`https://map-tiles.open-meteo.com/data_spatial/${selectedDomain.path}/latest.json?time_step=valid_times_${selectedHourIndex}&variable=${selectedWeatherVariable}`}
              />
            )}
          </ZLayer>

          <ZLayer id="dynamic-base-map" beforeId="dynamic-conditions">
            <GMapsBaseLayer idPrefix="google-base" layer={googleBaseLayer} />
          </ZLayer>
        </Map>
      </main>
    </div>
  );
}

export default App;
